// formula-settings.js

// Configuration
const CONFIG = {
    TEST_MODE: true,
    BASE_URL: 'https://babybrezzaserver.com/index.php/filterproduct_aws_upc_2_new_theme',
    DEBUG: true
};

// Translation Service
class TranslationService {
    constructor() {
        this.translations = {
            // Default English translations
            brand: 'Brand',
            type: 'Type',
            territory: 'Territory',
            stage: 'STAGE',
            setting: 'SETTING',
            noResults: 'No Results Found For Barcode:',
            selectFormula: 'Please select formula brand/type above',
            resultsFor: 'Results for Barcode:'
        };
    }

    async loadTranslations(language) {
        try {
            // Skip fetching if we're on English as it's the default
            if (language === 'en') {
                // console.log('Using default English translations');
                return;
            }

            const response = await fetch(`/translations/${language}.json`);

            if (!response.ok) {
                console.warn(`Translations file not found for language: ${language}. Using defaults.`);
                return;
            }

            const text = await response.text();

            // Check if the response is empty or invalid
            if (!text || text.trim() === '') {
                console.warn(`Empty translations file for language: ${language}. Using defaults.`);
                return;
            }

            // Parse the JSON text
            this.translations = JSON.parse(text);
            // console.log(`Successfully loaded translations for ${language}`);
        } catch (error) {
            // console.warn(`Error loading translations for ${language}:`, error);
            // Keep using default translations - already set in constructor
        }
    }

    get(key) {
        return this.translations[key] || key;
    }
}

// Cache Service
class CacheService {
    constructor() {
        this.cache = new Map();
    }

    async get(key, fetchFn, ttl = 300000) { // 5 minutes default TTL
        if (this.cache.has(key)) {
            const { data, timestamp } = this.cache.get(key);
            if (Date.now() - timestamp < ttl) {
                return data;
            }
        }

        const data = await fetchFn();
        this.cache.set(key, { data, timestamp: Date.now() });
        return data;
    }
}

// Utility Functions
const utils = {
    log: (...args) => {
        if (CONFIG.DEBUG) {
            // console.log('[Formula Settings]', ...args);
        }
    },

    error: (...args) => {
        if (CONFIG.DEBUG) {
            // console.error('[Formula Settings]', ...args);
        }
    },

    getCookie: (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return '';
    },

    scrollToElement: (selector, offset = 0) => {
        const element = document.querySelector(selector);
        if (element) {
            // Get the element's position
            const rect = element.getBoundingClientRect();
            const elementTop = rect.top + window.pageYOffset;

            // Apply offset and scroll
            window.scrollTo({
                top: elementTop + offset,
                behavior: 'smooth'
            });
        }
    }
};

// Main Formula Settings Class
class FormulaSettings {
    constructor() {
        this.initializeElements();
        this.attachEventListeners();
        this.translationService = new TranslationService();
        this.cacheService = new CacheService();
        this.testMode = CONFIG.TEST_MODE;
        this.loadTranslations();
        this.detected_upcs = [];
        this.territoryAbortController = null;
        this.brandsAbortController = null;
        this.typesAbortController = null;
        this.settingsAbortController = null;

        // Warranty form variables
        this.firstName = "";
        this.lastName = "";
        this.email = "";
        this.submissionId = null;
        this.clientId = 50;
        this.discoInit = false;

        // Hide sections on page load
        this.hideInitialSections();

        // Initialize warranty form handlers
        this.initializeWarrantyForm();

        // Setup scan triggers with a slight delay to ensure DOM is ready
        setTimeout(() => this.setupScanTriggers(), 300);
    }

    async loadTranslations() {
        const language = document.documentElement.lang || 'en';
        await this.translationService.loadTranslations(language);
    }

    initializeElements() {
        // Main containers
        this.upcResults = document.getElementById('upc_results');
        this.upcSettings = document.getElementById('upc_settings');
        this.findStageSetting = document.getElementById('find_stage_setting');
        this.formulaReferenceImages = document.getElementById('formula-reference-images');

        // Form elements
        this.upcInput = document.getElementById('upc_input');
        this.emailInput = document.getElementById('enter_email_input');
        this.territorySelect = document.getElementById('sel_territory');
        this.brandSelect = document.getElementById('sel_brand');
        this.typeSelect = document.getElementById('sel_type');

        // Hidden fields
        this.territoryId = document.getElementById('sel_territory_id');
        this.brandId = document.getElementById('sel_brand_id');
        this.typeId = document.getElementById('sel_type_id');

        // Product elements
        this.productElements = document.querySelectorAll('.formula-settings-banner__product');
        this.modelTypeInput = document.getElementById('model_type');
        this.productSelectionDiv = document.getElementById('productSelectionDiv');
        this.warrantyRegiDiv = document.getElementById('warranty_regi_div');
        this.formulaProFilter = document.getElementById('formula_pro_filter');

        // Scanner related elements
        this.interactiveElement = document.getElementById('interactive');
        this.upcEnterButton = document.getElementById('upc_enter');

        // Try to find the barcode scanner button in multiple ways
        this.startUpcButton = document.getElementById('start_upc');
        if (!this.startUpcButton) {
            // console.log('Could not find scanner button with ID "start_upc", trying alternative IDs...');
            this.startUpcButton = document.getElementById('upc_start');
        }

        // Log scanner element status
        // console.log('Scanner interactive element found:', !!this.interactiveElement);
        // console.log('UPC enter button found:', !!this.upcEnterButton);
        // console.log('Scanner start button found:', !!this.startUpcButton);

        // Warranty form elements
        this.registerProductForm = document.querySelector('.register-product-form');
        this.reviewForm = document.querySelector('.submit-review-form');
        this.noThanksLinks = document.querySelectorAll('.noThanks');

        // Make sure we're getting the correct register-product and submit-review elements
        // Try different selector approaches to ensure we find them
        this.registerProduct = document.querySelector('.register-product');
        if (!this.registerProduct) {
            this.registerProduct = document.querySelector('#warranty_regi_div .register-product');
            // console.log('Found register product with alternative selector:', !!this.registerProduct);
        }

        this.submitReview = document.querySelector('.submit-review');
        if (!this.submitReview) {
            this.submitReview = document.querySelector('#warranty_regi_div .submit-review');
            // console.log('Found submit review with alternative selector:', !!this.submitReview);
        }

        // console.log('Initialization - Register Product Element:', this.registerProduct);
        // console.log('Initialization - Submit Review Element:', this.submitReview);
    }

    attachEventListeners() {
        //Checkbox Klaviyo - Lissstrak
        const checkbox = document.getElementById('consent_checkbox_1');
        if (checkbox) {
            checkbox.addEventListener('change', function () {
                // console.log("Consent is changed")
                this.addInformationToListrak()
            });
        }

        // UPC input handling
        this.upcInput?.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                this.handleUPCSubmit();
            }
        });

        if (this.upcInput && this.upcEnterButton) {
            this.upcEnterButton.addEventListener('click', () => {
                this.handleUPCSubmit();
            });
        }

        // Email input validation on input change
        this.emailInput?.addEventListener('input', () => {
            // Validate email as user types and remove error if valid
            this.validateEmail();
        });

        // Barcode scanner buttons are now handled in setupScanTriggers method

        // Territory selection
        this.territorySelect?.addEventListener('change', (e) => {
            if (!this.validateEmail()) {
                this.showError('Please enter a valid email address');
                this.territorySelect.selectedIndex = 0;
                this.emailInput.focus();
                return;
            }
            this.handleTerritoryChange(e.target.value);
        });

        // Brand selection
        this.brandSelect?.addEventListener('change', (e) => {
            this.handleBrandChange(e.target.value);
        });

        // Type selection
        this.typeSelect?.addEventListener('change', (e) => {
            this.handleTypeChange(e.target.value);
        });

        // Add product selection handler
        this.productElements.forEach(product => {
            product.addEventListener('click', () => this.handleProductSelection(product));
            product.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    this.handleProductSelection(product);
                }
            });
        });
    }

    initBarcodeScanner() {
        if (!this.validateEmail()) {
            this.showError('Please enter a valid email address');
            return;
        }
        this.addInformationToListrak()

        const resultElement = document.getElementById('result');
        if (resultElement) resultElement.innerHTML = "";

        navigator.mediaDevices.enumerateDevices()
            .then(devices => {
                const hasCamera = devices.some(device => device.kind === 'videoinput');

                if (hasCamera) {
                    Quagga.init({
                        inputStream: {
                            name: "Live",
                            type: "LiveStream",
                            target: this.interactiveElement,
                            constraints: {
                                facingMode: "environment" // or "user" for front camera
                            },
                        },
                        decoder: {
                            readers: ["upc_reader", "ean_reader"], // or other supported barcode types
                        },
                        locator: {
                            patchSize: 5,
                            halfSample: false,
                        },
                        numOfWorkers: navigator.hardwareConcurrency || 1,
                        frequency: 20,
                        locate: true,
                    }, err => {
                        if (err) {
                            console.error('Quagga initialization error:', err);
                            this.showError('Error starting the barcode scanner: ' + err.message);
                            return;
                        }

                        // console.log("Quagga initialization finished. Ready to start");
                        Quagga.start();
                        if (this.interactiveElement) {
                            this.interactiveElement.style.display = 'block';
                            setTimeout(() => {
                                scrollToElement(".scanner-wrapper", false, 120)
                            },1000)
                        }

                        // Setup Quagga event handlers
                        this.setupQuaggaHandlers();
                    });
                } else {
                    if (resultElement) {
                        resultElement.innerHTML = '<p style="font-size: 16px; font-weight: 700; text-transform: uppercase; margin:0; color: red;">No Camera Detected</p>';
                    } else {
                        this.showError('No camera detected on your device');
                    }
                }
            })
            .catch(err => {
                console.error("Error accessing media devices:", err);
                this.showError('Error accessing camera: ' + err.message);
            });
    }

    setupQuaggaHandlers() {
        // Handle detected barcode
        Quagga.onDetected(result => {
            if (this.detected_upcs.includes(result.codeResult.code)) {
                this.detected_upcs = [];
                // Handle the detected barcode
                if (this.upcInput) {
                    this.upcInput.value = result.codeResult.code;
                }
                Quagga.stop();
                if (this.interactiveElement) {
                    this.interactiveElement.style.display = 'none';
                }
                this.handleUPCSubmit();
            } else {
                this.detected_upcs.push(result.codeResult.code);
            }
        });

        // Process barcode scanning results
        Quagga.onProcessed(result => {
            if (!result) return;

            const drawingCtx = Quagga.canvas.ctx.overlay;
            const drawingCanvas = Quagga.canvas.dom.overlay;

            if (drawingCtx && drawingCanvas) {
                drawingCtx.clearRect(0, 0, parseInt(drawingCanvas.getAttribute("width")), parseInt(drawingCanvas.getAttribute("height")));

                if (result.box) {
                    Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, { color: "#00F", lineWidth: 2 });
                }

                if (result.codeResult && result.codeResult.code) {
                    // console.log("Barcode detected: " + result.codeResult.code);
                }
            }
        });
    }

    async handleUPCSubmit() {
        if (!this.validateEmail()) {
            this.showError('Please enter a valid email address');
            return;
        }
        this.addInformationToListrak()

        const upc = this.upcInput.value.trim().replace(/[^a-z0-9]/gi, '');
        if (!upc) {
            this.showError('Please enter a UPC code');
            return;
        }

        try {
            const response = await this.fetchUPCData(upc);
            this.displayUPCResults(response);
        } catch (error) {
            this.showError('Error fetching UPC data');
            utils.error('UPC Error:', error);
        }
    }

    async fetchUPCData(upc) {
        const formData = new URLSearchParams();
        formData.append('upc', upc);
        formData.append('email', this.emailInput.value);
        formData.append('ip_address', utils.getCookie('ip_address'));
        formData.append('user_agent', navigator.userAgent);
        formData.append('touch_device', 'ontouchstart' in window || navigator.maxTouchPoints > 0);
        formData.append('state_prov', utils.getCookie('state_prov'));
        formData.append('zip', utils.getCookie('zip'));
        formData.append('country', utils.getCookie('country'));
        formData.append('city', utils.getCookie('city'));
        formData.append('babyb_store', 'babybrezza.com');

        const response = await fetch(`${CONFIG.BASE_URL}/getupc`, {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    }

    displayUPCResults(data) {
        if (!data.found) {
            this.upcSettings.innerHTML = `
                <h2 style="text-transform: none; margin-top: 20px; margin-bottom: 15px; font-size: 20px;">
                    <strong>${this.translationService.get('noResults')}</strong> ${data.upc}<br>
                    ${this.translationService.get('selectFormula')}
                </h2>`;
            this.findStageSetting.innerHTML = '';

            // Clear reference images when no UPC results found
            if (this.formulaReferenceImages) {
                this.formulaReferenceImages.innerHTML = '';
            }
            // Ensure results containers are visible for UPC flow
            const resultsSection = document.querySelector('.formula-settings-finder__results');
            if (resultsSection) {
                resultsSection.style.display = 'block';
                resultsSection.setAttribute('aria-hidden', 'false');
            }
            if (this.upcResults) {
                this.upcResults.style.display = 'block';
                this.upcResults.setAttribute('aria-hidden', 'false');
            }
            // Scroll to results so the message is visible
            utils.scrollToElement('.formula-settings-finder__results', -105);
            return;
        }

        this.upcSettings.innerHTML = `
            <h2 style="text-transform: none; margin-top: 20px; margin-bottom: 15px; font-size: 20px;">
                <strong>${this.translationService.get('resultsFor')}</strong> ${data.upc}
            </h2>`;

        // Generate HTML for settings table without images
        const html = this.generateFormulaHTML(data);
        this.findStageSetting.innerHTML = html;
        this.upcResults.style.display = 'block';
        this.upcResults.setAttribute('aria-hidden', 'false');

        // Ensure the overall results section is visible when using UPC flow
        const resultsSection = document.querySelector('.formula-settings-finder__results');
        if (resultsSection) {
            resultsSection.style.display = 'block';
            resultsSection.setAttribute('aria-hidden', 'false');
        }

        // Populate reference images container with images from the UPC data
        if (this.formulaReferenceImages && data.images && data.images.length > 0) {
            this.formulaReferenceImages.innerHTML = this.generateImagesHTML(data.images);

            // Make sure any images or notes wrappers in the stage settings wrapper are removed
            if (this.findStageSetting) {
                const elementsToRemove = this.findStageSetting.querySelectorAll(
                    '.formula-image-wrapper, .formula-images-container, .settings-note-wrapper'
                );
                elementsToRemove.forEach(el => el.remove());
            }
        } else if (this.formulaReferenceImages) {
            this.formulaReferenceImages.innerHTML = '<p>No reference images available for this formula.</p>';
        }

        // Scroll to results so the user sees the UPC outcome immediately
        utils.scrollToElement('.formula-settings-finder__results', -105);
    }

    // Clear UPC results when switching to manual dropdown flow
    clearUPCResults() {
        try {
            if (this.upcSettings) {
                this.upcSettings.innerHTML = '';
            }
            if (this.upcResults) {
                this.upcResults.style.display = 'none';
                this.upcResults.setAttribute('aria-hidden', 'true');
            }
        } catch (e) {
            // no-op
        }
    }

    generateFormulaHTML(data) {
        return `
            <div class="upc-result-wrapper">
                <h3>
                    <strong>${this.translationService.get('brand')}:</strong> ${data.brand}<br>
                    <strong>${this.translationService.get('type')}:</strong> ${data.type}<br>
                    <strong>${this.translationService.get('territory')}:</strong> ${data.territory}
                </h3>
                <table>
                    <tr>
                        <th>${this.translationService.get('stage')}</th>
                        <th>${this.translationService.get('setting')}</th>
                    </tr>
                    <tr>
                        <td class="${data.stage === 'N/A' ? 'error' : ''}">${data.stage}</td>
                        <td class="${data.setting === 'N/A' ? 'error' : ''}">${data.setting}</td>
                    </tr>
                </table>
            </div>`;
    }

    generateImagesHTML(images) {
        if (!images || !images.length) return '';

        return images.map(image => `
            <div class="formula-image-wrapper">
                <img class="formula-image" 
                    style="display: block; margin-left: auto; margin-right: auto; max-width: 250px; max-height: 300px;" 
                    alt="${image}" 
                    src="https://babybrezzacloud.com/uploads/formulaprotool/original/${image}"
                    loading="lazy"/>
            </div>
        `).join('');
    }
    initDisco(){
        const email = document.getElementById('enter_email_input').value;
        let selectedType = document.querySelector('.formula-settings-banner__product.active').getAttribute("data-type");
        let selectedId = document.querySelector('.formula-settings-banner__product.active').getAttribute("data-product-id");
        let selectName = document.querySelector('.formula-settings-banner__product.active').getAttribute("data-product-name");
        let first_name = document.querySelector('.register-product-form .first-name-container input').value;
        if(first_name === ""){
            first_name = "Default First Name";
        }
        let last_name = document.querySelector('.register-product-form .Last-name-container input').value;
        if(last_name === ""){
            last_name = "Default Last Name";
        }
        window.discoLauncher.loadWidget({
            email: email,
            first_name: first_name,
            last_name: last_name,
            purchases: [
                {
                    product_id: selectedId,
                    type: selectedType,
                    name: selectName,
                    quantity: "1",
                },
            ],
            order_id: "123456",
            page_type: "order_status",
        });
    }
    async addInformationToListrak() {
        const optInEl = document.getElementById('klaviyo-optin');
        if (!optInEl || !optInEl.checked) {
            return;
        }
        const emailInputEl = document.getElementById('enter_email_input');
        const email = emailInputEl ? emailInputEl.value : '';
        if (email) {
            // const selectedProduct = document.getElementById('selected-product-title').textContent;

            try {
                let params = {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        email: email
                    })
                };
                // params.body.selectedProduct = selectedProduct;
                const response = await fetch("https://optin.babybrezza.com/api/subscribe/listrak", params);

                if (!response.ok) {
                    const error = await response.text();
                    console.log(error)
                    return
                }
            } catch (err) {
                console.log(err)
            }
        }

    }
    async addInformationToKlaviyo() {
        const optInEl = document.getElementById('klaviyo-optin');
        if (!optInEl || !optInEl.checked) {
            return;
        }
        const emailInputEl = document.getElementById('enter_email_input');
        const email = emailInputEl ? emailInputEl.value : '';
        const selectedTitleEl = document.getElementById('selected-product-title');
        const selectedProduct = selectedTitleEl ? selectedTitleEl.textContent : '';

        if (email) {
            try {
                let params = {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "revision": "2023-10-15"
                    },
                    body: JSON.stringify({
                        email: email
                    })
                };
                params.body.selectedProduct = selectedProduct;
                const response = await fetch("https://review-puller.onrender.com/add-optin-klaviyo", );

                if (!response.ok) {
                    const error = await response.text();
                    console.log(error)
                    return
                }
            } catch (err) {
                console.log(err)
            }
        }

    }

    updateMissingLink(country){
        const activeProductEl = document.querySelector('.formula-settings-banner__product.active');
        const productName = activeProductEl ? activeProductEl.getAttribute("data-product-name") : '';
        const params = new URLSearchParams();
        if (productName) params.append('product', productName);
        if (country) params.append('country', country);
        const link = `https://knowledge.babybrezza.com/s/formula-settings?${params.toString()}`;
        const brandLinkEl = document.getElementById("missing_brand_brand");
        if (brandLinkEl) brandLinkEl.setAttribute("href", link);
        const typeLinkEl = document.getElementById("missing_brand_type");
        if (typeLinkEl) typeLinkEl.setAttribute("href", link);
    }

    async handleTerritoryChange(territory) {
        // Clear leftover UPC result when switching to manual selection
        this.clearUPCResults();
        if (!this.validateEmail()) {
            this.territorySelect.selectedIndex = 0;
            this.emailInput.focus();
            return;
        }

        if (!territory) {
            console.error('No territory selected');
            return;
        }

        // Format territory for URL encoding
        let formattedTerritory = territory;
        document.getElementById("choose_brand_link").style.display = "inline-block";
        this.updateMissingLink(territory);
        // Set hidden field
        if (this.territoryId) {
            this.territoryId.value = formattedTerritory;
        } else {
            console.error('Territory ID field not found');
        }

        // Reset brand and type selects
        if (this.brandSelect) {
            this.brandSelect.innerHTML = '<option value="">-- Choose Your Formula Brand --</option>';
        }

        if (this.typeSelect) {
            this.typeSelect.innerHTML = '<option value="">-- Choose Your Formula Type --</option>';
        }

        if (this.findStageSetting) {
            this.findStageSetting.innerHTML = '';
        }

        try {
            const response = await this.fetchBrands(formattedTerritory);
            this.updateBrandSelect(response);
        } catch (error) {
            if (error.name !== 'AbortError') {
                this.showError('Error fetching brands');
                utils.error('Brand Error:', error);
            }
        }
    }

    async fetchBrands(territory) {
        if (this.brandsAbortController) {
            this.brandsAbortController.abort();
        }
        this.brandsAbortController = new AbortController();
        const signal = this.brandsAbortController.signal;

        // Always use 'advanced' regardless of what's in the modelTypeInput
        const modelType = 'advanced';
        const cacheKey = `brands_${territory}_${modelType}`;

        return await this.cacheService.get(cacheKey, async () => {
            const formData = new URLSearchParams();
            formData.append('territory_id', territory);
            formData.append('model_type', modelType);
            formData.append('language', document.documentElement.lang || 'en');

            try {
                const response = await fetch(`${CONFIG.BASE_URL}/getbrand`, {
                    method: 'POST',
                    body: formData,
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    },
                    signal
                });

                if (!response.ok) {
                    console.error('Error response:', response.status, response.statusText);
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const contentType = response.headers.get('content-type');
                let data;

                if (contentType && contentType.indexOf('application/json') !== -1) {
                    // Handle JSON response (new API)
                    data = await response.json();
                    return data;
                } else {
                    // Handle HTML response (old API)
                    const html = await response.text();

                    // Create a temporary element to parse the HTML
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = html;

                    // Extract the options from the HTML
                    const defaultLabel = tempDiv.querySelector('option')?.textContent || 'Choose Your Formula Brand';
                    const brands = [];

                    // Get all options except the first one (default)
                    const options = tempDiv.querySelectorAll('option');
                    for (let i = 1; i < options.length; i++) {
                        if (options[i].value) {
                            brands.push(decodeURIComponent(options[i].value));
                        }
                    }

                    // Return in the expected format
                    return {
                        defaultLabel,
                        brands
                    };
                }
            } catch (error) {
                console.error('Fetch error:', error);
                throw error;
            }
        });
    }

    updateBrandSelect(data) {
        if (!this.brandSelect) {
            return;
        }

        let html = `<option value="">${data.defaultLabel || 'Choose Your Formula Brand'}</option>`;

        if (Array.isArray(data.brands)) {
            data.brands.forEach(brand => {
                // Use the raw brand name as value and display text
                html += `<option value="${brand}">${brand}</option>`;
            });

            this.brandSelect.innerHTML = html;
        } else {
            this.showError('Error: Invalid brand data received from server');
        }
    }

    validateEmail() {
        if (!this.emailInput) return true;

        const email = this.emailInput.value;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (emailRegex.test(email)) {
            this.emailInput.classList.remove('invalid');
            // Remove any existing error messages when email is valid
            const existingErrors = document.querySelectorAll('.formula-settings-error');
            existingErrors.forEach(err => err.remove());
            return true;
        } else {
            this.emailInput.classList.add('invalid');
            return false;
        }
    }

    showError(message) {
        utils.error(message);

        // Display error to the user
        const errorDiv = document.createElement('div');
        errorDiv.className = 'formula-settings-error';
        errorDiv.style.color = 'red';
        errorDiv.style.padding = '1rem';
        errorDiv.style.marginTop = '1rem';
        errorDiv.style.border = '1px solid red';
        errorDiv.style.borderRadius = '0.25rem';
        errorDiv.style.backgroundColor = 'rgba(255, 0, 0, 0.1)';
        errorDiv.textContent = message;

        // Remove any existing error messages
        const existingErrors = document.querySelectorAll('.formula-settings-error');
        existingErrors.forEach(err => err.remove());

        // Add the error message after the email input
        if (this.emailInput) {
            this.emailInput.parentNode.appendChild(errorDiv);
        } else if (this.findStageSetting) {
            this.findStageSetting.innerHTML = '';
            this.findStageSetting.appendChild(errorDiv);
        }
    }

    handleProductSelection(product) {
        if (this.territoryAbortController) this.territoryAbortController.abort();
        if (this.brandsAbortController) this.brandsAbortController.abort();
        if (this.typesAbortController) this.typesAbortController.abort();
        if (this.settingsAbortController) this.settingsAbortController.abort();

        const modelType = product.dataset.modelType;
        const productId = product.dataset.productId;

        if (!modelType) {
            this.showError('Error: Model type not found. Please ensure products are configured correctly.');
            return;
        }
        document.getElementById("choose_brand_link").style.display = "none";
        document.getElementById("choose_brand_type").style.display = "none";
        // Update selected state
        this.productElements.forEach(p => p.classList.remove('active'));
        product.classList.add('active');

        // Update the selected product title in the filter section
        const selectedProductTitle = document.getElementById('selected-product-title');
        const warrantyProductTitle = document.getElementById('warranty-product-title');
        if (selectedProductTitle) {
            // Get the product title from the selected product element
            const productTitle = product.querySelector('.product-title-wrapper p').textContent.trim();
            selectedProductTitle.textContent = productTitle;

            // Also update the warranty form product title
            if (warrantyProductTitle) {
                warrantyProductTitle.textContent = productTitle;
            }
        }

        // Always use 'advanced' as the model type for server requests
        const serverModelType = 'advanced';

        // Set the model type for the form
        if (this.modelTypeInput) {
            this.modelTypeInput.value = serverModelType;
        } else {
            this.showError('Error: Model type input field not found. Please check your page configuration.');
            return;
        }

        // Update product selection in contact form if it exists
        const contactFormProduct = document.querySelector('#contactFormProduct');
        if (contactFormProduct) {
            const option = contactFormProduct.querySelector(`option[value="${productId}"]`);
            if (option) {
                option.selected = true;
            }
        }

        // Reset all selects and hidden fields
        if (this.territorySelect) {
            this.territorySelect.innerHTML = '<option value="">-- Choose Your Territory --</option>';
            // this.territorySelect.innerHTML = '<option value="">-- Choose Your Territory --</option><option value="1">test</option>';
        }

        if (this.brandSelect) {
            this.brandSelect.innerHTML = '<option value="">-- Choose Your Formula Brand --</option>';
        }

        if (this.typeSelect) {
            this.typeSelect.innerHTML = '<option value="">-- Choose Your Formula Type --</option>';
        }

        if (this.territoryId) this.territoryId.value = '';
        if (this.brandId) this.brandId.value = '';
        if (this.typeId) this.typeId.value = '';

        if (this.findStageSetting) {
            this.findStageSetting.innerHTML = '';
        }

        // Hide product selection and show warranty registration if they exist
        if (this.productSelectionDiv) {
            this.productSelectionDiv.style.display = 'none';
        }

        // Show warranty registration div
        if (this.warrantyRegiDiv) {
            this.warrantyRegiDiv.style.display = 'block';
            this.warrantyRegiDiv.setAttribute('aria-hidden', 'false');

            // Reset the forms if they exist
            if (this.registerProductForm) this.registerProductForm.reset();
            if (this.reviewForm) this.reviewForm.reset();

            // Show registration form and hide review form
            if (this.registerProduct) this.registerProduct.style.display = 'block';
            if (this.submitReview) this.submitReview.style.display = 'none';

            // Hide formula filter section until warranty registration is complete
            if (this.formulaProFilter) {
                this.formulaProFilter.style.display = 'none';
                this.formulaProFilter.setAttribute('aria-hidden', 'true');
            }
        } else {
            // If no warranty registration div, show formula filter directly
            if (this.formulaProFilter) {
                this.formulaProFilter.style.display = 'block';
                this.formulaProFilter.setAttribute('aria-hidden', 'false');
            }
        }

        // Get territory data for the selected model
        try {
            this.getTerritoryData();
        } catch (error) {
            this.showError('Error fetching territories. Please try again later.');
        }

        // Scroll to warranty form or formula filter section with offset
        if (this.warrantyRegiDiv && this.warrantyRegiDiv.style.display !== 'none') {
            utils.scrollToElement('#warranty_regi_div', -105); // Scroll 100px above the element
        } else if (this.formulaProFilter) {
            utils.scrollToElement('#formula_pro_filter', -105); // Scroll 100px above the element
        }

        // Ensure CTA and results sections remain hidden
        const ctaSection = document.querySelector('.formula-settings-finder__cta');
        if (ctaSection) {
            ctaSection.style.display = 'none';
        }

        const resultsSection = document.querySelector('.formula-settings-finder__results');
        if (resultsSection) {
            resultsSection.style.display = 'none';
        }
    }

    getTerritoryData() {
        if (this.territoryAbortController) {
            this.territoryAbortController.abort();
        }
        this.territoryAbortController = new AbortController();
        const signal = this.territoryAbortController.signal;

        // Always use 'advanced' regardless of what's in the modelTypeInput
        const modelType = 'advanced';

        const formData = new URLSearchParams();
        formData.append('model_type', modelType);
        formData.append('language', document.documentElement.lang || 'en');

        fetch(`${CONFIG.BASE_URL}/index`, {
            method: 'POST',
            body: formData,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            signal
        })
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.indexOf('application/json') !== -1) {
                return response.json();
            } else {
                return response.text().then(text => {
                    throw new Error('Invalid response format');
                });
            }
        })
        .then(data => {
            if (data && (data.territories || (data.defaultLabel && Array.isArray(data.territories)))) {
                this.updateTerritorySelect(data);
                this.addInformationToListrak()
                // Update brand and type select placeholders with translated labels
                if (this.brandSelect) {
                    const brandOption = this.brandSelect.querySelector('option');
                    if (brandOption) {
                        brandOption.textContent = data.brandLabel || this.translationService.get('brand');
                    }
                }
                if (this.typeSelect) {
                    const typeOption = this.typeSelect.querySelector('option');
                    if (typeOption) {
                        typeOption.textContent = data.typeLabel || this.translationService.get('type');
                    }
                }
            } else {
                throw new Error('Invalid territory data format');
            }
        })
        .catch(error => {
            if (error.name !== 'AbortError') {
                this.showError('Error fetching territories: ' + error.message);
            }
        });
    }

    updateTerritorySelect(data) {
        if (!this.territorySelect) {
            return;
        }

        let html = `<option value="">${data.defaultLabel || 'Choose Your Territory'}</option>`;

        if (Array.isArray(data.territories)) {
            data.territories.forEach(territory => {
                html += `<option value="${encodeURIComponent(territory)}">${territory}</option>`;
            });

            this.territorySelect.innerHTML = html;
        } else {
            this.showError('Error: Invalid territory data received from server');
        }
    }

    async handleBrandChange(brandId) {
        // Clear leftover UPC result when switching to manual selection
        this.clearUPCResults();
        if (!this.validateEmail()) {
            this.brandSelect.selectedIndex = 0;
            this.emailInput.focus();
            return;
        }

        // Format brand ID consistently with the old implementation
        let formattedBrandId = brandId;
        if (brandId && !brandId.includes('%20') && brandId.includes(' ')) {
            // If brand has spaces but isn't URL encoded, encode it similar to old code
            formattedBrandId = brandId.replace(/ /g, '%20');
        }

        // Set hidden field
        if (this.brandId) {
            this.brandId.value = formattedBrandId;
        }

        document.getElementById("choose_brand_type").style.display = "inline-block";
        this.updateMissingLink(this.territorySelect.value);
        // Reset type select
        if (this.typeSelect) {
            this.typeSelect.innerHTML = '<option value="">-- Choose Your Formula Type --</option>';
        }

        if (this.findStageSetting) {
            this.findStageSetting.innerHTML = '';
        }

        try {
            const response = await this.fetchTypes(formattedBrandId);
            this.updateTypeSelect(response);
            this.addInformationToListrak()
        } catch (error) {
            if (error.name !== 'AbortError') {
                this.showError('Error fetching formula types');
                utils.error('Type Error:', error);
            }
        }
    }

    async fetchTypes(brandId) {
        if (this.typesAbortController) {
            this.typesAbortController.abort();
        }
        this.typesAbortController = new AbortController();
        const signal = this.typesAbortController.signal;

        const territory = this.territoryId ? this.territoryId.value : '';
        // Always use 'advanced' regardless of what's in the modelTypeInput
        const modelType = 'advanced';

        // Ensure we keep raw URL encoded values similar to old implementation
        let formattedBrandId = brandId;
        if (brandId && !brandId.includes('%20') && brandId.includes(' ')) {
            // If brand has spaces but isn't URL encoded, encode it similar to old code
            formattedBrandId = brandId.replace(/ /g, '%20');
        }

        const formData = new URLSearchParams();
        formData.append('brand_id', formattedBrandId);
        formData.append('territory_id', territory);
        formData.append('model_type', modelType);
        formData.append('language', document.documentElement.lang || 'en');

        try {
            const response = await fetch(`${CONFIG.BASE_URL}/gettypepro`, {
                method: 'POST',
                body: formData,
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                signal
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const contentType = response.headers.get('content-type');

            if (contentType && contentType.indexOf('application/json') !== -1) {
                // Handle JSON response (new API)
                const data = await response.json();
                return data;
            } else {
                // Handle HTML response (old API)
                const html = await response.text();

                // Create a temporary element to parse the HTML
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = html;

                // Extract the options from the HTML
                const defaultLabel = tempDiv.querySelector('option')?.textContent || 'Choose Your Formula Type';
                const types = [];

                // Get all options except the first one (default)
                const options = tempDiv.querySelectorAll('option');
                for (let i = 1; i < options.length; i++) {
                    if (options[i].value) {
                        types.push(decodeURIComponent(options[i].value));
                    }
                }

                // Return in the expected format
                return {
                    defaultLabel,
                    types
                };
            }
        } catch (error) {
            throw error;
        }
    }

    updateTypeSelect(data) {
        if (!this.typeSelect) {
            return;
        }

        let html = `<option value="">${data.defaultLabel || 'Choose Your Formula Type'}</option>`;

        if (Array.isArray(data.types)) {
            data.types.forEach(type => {
                // Use the raw type name as value and display text
                html += `<option value="${type}">${type}</option>`;
            });

            this.typeSelect.innerHTML = html;
        } else {
            this.showError('Error: Invalid type data received from server');
        }
    }

    async handleTypeChange(typeId) {
        // Clear leftover UPC result when switching to manual selection
        this.clearUPCResults();
        if (!this.validateEmail()) {
            this.typeSelect.selectedIndex = 0;
            this.emailInput.focus();
            return;
        }

        // Handle dynamic listing section visibility
        const dynamicListingSection = document.querySelector('.section--dynamic-listing');
        const resultDiscSection = document.querySelector('.result-disc-section');
        if (dynamicListingSection) {
            dynamicListingSection.style.display = 'none'; // Keep hidden initially
        }

        // Set hidden field
        if (this.typeId) {
            this.typeId.value = typeId;
        }

        if (this.findStageSetting) {
            this.findStageSetting.innerHTML = '<div class="loading">Loading...</div>';
        }

        try {
            const response = await this.fetchSettings(typeId);
            this.displaySettings(response);

            // Show CTA section first
            const ctaSection = document.querySelector('.formula-settings-finder__cta');
            if (ctaSection) {
                ctaSection.style.display = 'block';
            }

            // Show results section
            const resultsSection = document.querySelector('.formula-settings-finder__results');
            if (resultsSection) {
                resultsSection.style.display = 'block';
            }

            // Finally show dynamic listing section after a short delay to ensure proper sequence
            setTimeout(() => {
                this.addInformationToListrak()
                if (dynamicListingSection) {
                    dynamicListingSection.style.display = 'block';
                }
                if (resultDiscSection) {
                    resultDiscSection.style.display = 'block';
                }
            }, 100);

        } catch (error) {
            if (error.name !== 'AbortError') {
                this.showError('Error fetching formula settings');
                utils.error('Settings Error:', error);
            }
        }
    }

    async fetchSettings(typeId) {
        if (this.settingsAbortController) {
            this.settingsAbortController.abort();
        }
        this.settingsAbortController = new AbortController();
        const signal = this.settingsAbortController.signal;

        const territory = this.territoryId ? this.territoryId.value : '';
        const brand = this.brandId ? this.brandId.value : '';
        // Always use 'advanced' regardless of what's in the modelTypeInput
        const modelType = 'advanced';
        const email = this.emailInput ? this.emailInput.value : '';

        const formData = new URLSearchParams();
        formData.append('type_id', typeId);
        formData.append('brand_id', brand);
        formData.append('territory_id', territory);
        formData.append('model_type', modelType);
        formData.append('email', email);
        formData.append('language', document.documentElement.lang || 'en');
        formData.append('ip_address', utils.getCookie('ip_address'));
        formData.append('user_agent', navigator.userAgent);
        formData.append('touch_device', 'ontouchstart' in window || navigator.maxTouchPoints > 0);
        formData.append('state_prov', utils.getCookie('state_prov'));
        formData.append('zip', utils.getCookie('zip'));
        formData.append('country', utils.getCookie('country'));
        formData.append('city', utils.getCookie('city'));
        formData.append('babyb_store', 'babybrezza.com');

        try {
            const response = await fetch(`${CONFIG.BASE_URL}/getsetting`, {
                method: 'POST',
                body: formData,
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                signal
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            if(this.discoInit === false){
                this.initDisco()
            }
            this.discoInit = true;

            // The getsetting endpoint returns HTML directly
            const html = await response.text();

            if (html.length < 10) {
                throw new Error('Empty or invalid response');
            }

            return html;
        } catch (error) {
            throw error;
        }
    }

    displaySettings(html) {
        if (!this.findStageSetting) {
            return;
        }

        // Wrap the HTML in a wrapper div
        const wrappedHtml = `<div class="formula-settings-result-wrapper">${html}</div>`;
        this.findStageSetting.innerHTML = wrappedHtml;

        // Extract formula images from the HTML response
        this.extractAndPopulateFormulaImages(html);

        // Show CTA and results sections
        const ctaSection = document.querySelector('.formula-settings-finder__cta');
        const resultsSection = document.querySelector('.formula-settings-finder__results');

        if (ctaSection) {
            ctaSection.style.display = 'block';
            ctaSection.setAttribute('aria-hidden', 'false');
        }

        if (resultsSection) {
            resultsSection.style.display = 'block';
            resultsSection.setAttribute('aria-hidden', 'false');
        }

        // Show any additional elements that need to be visible
        const enjoyMsg = document.getElementById('enjoy-msg');
        const fpsMailCta = document.getElementById('fps-mail-cta');
        const moreProductsWrapper = document.getElementById('more-products-wrapper');

        if (enjoyMsg) enjoyMsg.style.display = 'block';
        if (fpsMailCta) fpsMailCta.style.display = 'block';
        if (moreProductsWrapper) moreProductsWrapper.style.display = 'block';

        // Initialize any sliders if they exist
        if (window.$ && $('.more-slider').length) {
            try {
                $('.more-slider').slick({
                    slidesToShow: 5,
                    slidesToScroll: 1,
                    arrows: true,
                    dots: false,
                    infinite: false,
                    focusOnSelect: true,
                    draggable: true,
                    swipeToSlide: true,
                    responsive: [
                        {
                            breakpoint: 768,
                            settings: {
                                slidesToShow: 2
                            },
                        },
                        {
                            breakpoint: 1024,
                            settings: {
                                slidesToShow: 3
                            },
                        },
                        {
                            breakpoint: 1400,
                            settings: {
                                slidesToShow: 4
                            },
                        }
                    ]
                });
            } catch (error) {

            }
        }

        // Scroll to CTA section with -100px offset
        if (ctaSection) {
            utils.scrollToElement('.formula-settings-finder__cta', -105);
        } else {
            // Fallback to scrolling to results section if CTA not found
            utils.scrollToElement('.formula-settings-finder__results', -105);
        }
    }

    extractAndPopulateFormulaImages(html) {
        if (!this.formulaReferenceImages) {
            return;
        }

        try {
            // Create a temporary element to parse the HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;

            // First try looking in the formula-images-container (new structure)
            let formulaImagesContainer = tempDiv.querySelector('.formula-images-container');
            let formulaImages = formulaImagesContainer ?
                formulaImagesContainer.querySelectorAll('.formula-image-wrapper') : null;

            // If not found, try the old structure
            if (!formulaImages || formulaImages.length === 0) {
                formulaImages = tempDiv.querySelectorAll('.formula-image-wrapper');
            }

            if (formulaImages && formulaImages.length > 0) {
                // Clear existing images
                this.formulaReferenceImages.innerHTML = '';

                // Add each image to the reference container
                formulaImages.forEach(imageWrapper => {
                    // Only clone the image wrapper and its contents
                    const clone = imageWrapper.cloneNode(true);
                    this.formulaReferenceImages.appendChild(clone);
                });
            } else {
                // Try to find individual images if no .formula-image-wrapper elements are found
                const formulaImgs = tempDiv.querySelectorAll('img.formula-image');

                if (formulaImgs && formulaImgs.length > 0) {
                    // Clear existing images
                    this.formulaReferenceImages.innerHTML = '';

                    // Add each image to the reference container
                    formulaImgs.forEach(img => {
                        const wrapper = document.createElement('div');
                        wrapper.className = 'formula-image-wrapper';
                        wrapper.appendChild(img.cloneNode(true));
                        this.formulaReferenceImages.appendChild(wrapper);
                    });
                } else {
                    // If no images found, display a message
                    this.formulaReferenceImages.innerHTML = '<p>No reference images available for this formula.</p>';
                }
            }

            // Make sure any images in the stage settings wrapper are removed
            if (this.findStageSetting) {
                const stageSettingImgs = this.findStageSetting.querySelectorAll('.formula-image-wrapper, .formula-images-container, .settings-note-wrapper');
                stageSettingImgs.forEach(img => img.remove());
            }
        } catch (error) {
            console.error('Error extracting formula images:', error);
            this.formulaReferenceImages.innerHTML = '<p>Error loading reference images.</p>';
        }
    }

    initializeWarrantyForm() {
        // Get form elements
        this.registerProductForm = document.querySelector('.register-product-form');
        this.reviewForm = document.querySelector('.submit-review-form');
        this.noThanksLinks = document.querySelectorAll('.noThanks');
        this.registerProduct = document.querySelector('.register-product');
        this.submitReview = document.getElementById('submit_review');
        this.formulaProFilter = document.getElementById('formula_pro_filter');
        this.warrantyRegiDiv = document.getElementById('warranty_regi_div');

        // Add debug button if in debug mode
        // if (CONFIG.DEBUG) {
        //     this.addDebugButton();
        // }

        // Attach event listeners to forms
        if (this.registerProductForm) {
            this.registerProductForm.addEventListener('submit', this.handleRegisterationSubmit.bind(this));
        }

        if (this.reviewForm) {
            this.reviewForm.addEventListener('submit', this.handleReviewSubmit.bind(this));
        }

        if (this.noThanksLinks && this.noThanksLinks.length > 0) {
            this.noThanksLinks.forEach(link => {
                link.addEventListener('click', this.handleNoThanksClick.bind(this));
            });
        }
    }

    // Add debug button to test form transition
    addDebugButton() {
        const debugButton = document.createElement('button');
        debugButton.textContent = 'Debug: Test Form Transition';
        debugButton.style.cssText = 'position: fixed; top: 10px; right: 10px; z-index: 9999; background: red; color: white; padding: 5px;';
        debugButton.addEventListener('click', () => {

            // Force the warranty div to be visible
            if (this.warrantyRegiDiv) {
                this.warrantyRegiDiv.style.cssText = 'display: block !important; opacity: 1 !important; visibility: visible !important;';
                this.warrantyRegiDiv.removeAttribute('aria-hidden');
            }

            // Hide registration form
            if (this.registerProduct) {
                this.registerProduct.style.display = 'none';
            }

            // Show review form
            if (this.submitReview) {
                this.submitReview.style.cssText = 'display: block !important;';
            }
        });
        document.body.appendChild(debugButton);
    }

    // Handle registration form submission
    handleRegisterationSubmit(event) {
        event.preventDefault();

        // Get active product
        const activeProduct = document.querySelector('.formula-settings-banner__product.active');
        if (!activeProduct) {
            alert('Please select a product first');
            return;
        }

        const productID = activeProduct.getAttribute('data-product-id');

        // Show loading indicator
        const submitContainer = event.target.querySelector('.wholescale-submit-product-registration');
        if (submitContainer) {
            submitContainer.classList.add('active');
        }

        // Get form data
        const formData = new FormData(event.target);
        this.firstName = formData.get('first_name');
        this.lastName = formData.get('last_name');
        this.email = formData.get('email');

        // Create registration data object
        const registerationData = {
            first_name: this.firstName,
            last_name: this.lastName,
            email: this.email,
            product: productID,
            add_mailing: formData.get('is_subscribed') === 'on',
            terms_and_condition: true,
            platform: 'Direct Link',
            client: this.clientId,
            phone: null
        };

        // Only add black_box if it exists and is not empty
        try {
            if (window.blackBoxStr && window.blackBoxStr.trim() !== '') {
                registerationData.black_box = encodeURIComponent(window.blackBoxStr);
                utils.log('Added blackbox data to registration');
            } else {
                utils.log('BlackBox data is not available, proceeding without it');
            }
        } catch (error) {
            utils.error('Error adding blackbox data:', error);
        }

        utils.log('Registration Data:', registerationData);

        // For testing only - if in test mode, simulate a successful response
        if (CONFIG.TEST_MODE && CONFIG.DEBUG) {
            utils.log('TEST MODE: Validating API payload without sending');

            // Validate the payload
            const validationResult = this.validateRegistrationPayload(registerationData);
            if (!validationResult.isValid) {
                utils.error('Validation failed:', validationResult.errors);
                // alert(`API payload validation failed: ${validationResult.errors.join(', ')}`);

                // Remove active class from submit button
                if (submitContainer) {
                    submitContainer.classList.remove('active');
                }
                return;
            }

            // Log what would be sent to the API
            utils.log('API endpoint that would be called:', 'https://be.wholescale.net/review/external_registration');
            utils.log('HTTP method:', 'POST');
            utils.log('Headers that would be sent:', {
                'Content-Type': 'application/json'
            });
            utils.log('Request body that would be sent:', JSON.stringify(registerationData, null, 2));

            // Simulate response
            this.submissionId = 'test-' + Date.now();
            utils.log('Simulated response:', {
                submission_id: this.submissionId,
                status: 'success',
                message: 'Product registered successfully (test mode)'
            });
            showToast('Product Registered Successfully', 'success',"", "");

            // Make sure we have references to all necessary elements before transition
            this.warrantyRegiDiv = this.warrantyRegiDiv || document.getElementById('warranty_regi_div');
            this.registerProduct = this.registerProduct || document.querySelector('.register-product');
            this.submitReview = this.submitReview || document.getElementById('submit_review');

            utils.log('Elements before force transition:', {
                warrantyRegiDiv: !!this.warrantyRegiDiv,
                registerProduct: !!this.registerProduct,
                submitReview: !!this.submitReview
            });

            // Force the transition
            this.forceWarrantyFormTransition();

            // Remove active class from submit button
            if (submitContainer) {
                submitContainer.classList.remove('active');
            }
            return;
        }

        // Otherwise proceed with real API call
        this.submitRegisterationForm(registerationData, submitContainer);
    }

    // Validate registration payload
    validateRegistrationPayload(data) {
        const errors = [];

        // Check required fields
        if (!data.first_name) errors.push('First name is required');
        if (!data.last_name) errors.push('Last name is required');

        // Check email format
        if (!data.email) {
            errors.push('Email is required');
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(data.email)) {
                errors.push('Email format is invalid');
            }
        }

        // Check product ID
        if (!data.product) errors.push('Product ID is required');

        // Check client ID
        if (!data.client) errors.push('Client ID is required');

        // Confirm terms acceptance
        if (!data.terms_and_condition) errors.push('Terms and conditions must be accepted');

        return {
            isValid: errors.length === 0,
            errors: errors
        };
    }

    // Submit registration form to API
    submitRegisterationForm(registerationData, submitContainer) {
        const url = 'https://be.wholescale.net/review/external_registration';

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(registerationData)
        })
        .then(response => {
            if (!response.ok) {
                return response.json().then(data => {
                    utils.error('Registration error details:', data);
                    throw new Error(`Registration failed: ${JSON.stringify(data)}`);
                });
            }
            return response.json();
        })
        .then(data => {
            utils.log('Registration response:', data);

            // Check if we have a submission ID
            if (!data.submission_id) {
                throw new Error('No submission ID returned from the server');
            }

            // Store submission ID for review form
            this.submissionId = data.submission_id;

            // Show success toast
            showToast('Product Registered Successfully', 'success',"", "");

            // Force the transition to work properly
            this.forceWarrantyFormTransition();

            // Remove active class from submit button
            if (submitContainer) {
                submitContainer.classList.remove('active');
            }
        })
        .catch(error => {
            utils.error('Registration Error:', error);

            // Skip to formula finder if it's a blackbox error
            const errorMsg = error.message || '';
            if (errorMsg.includes('black_box') || errorMsg.includes('This field may not be blank')) {
                utils.log('Bypassing registration due to blackbox error');
                this.skipToFormulaFinder();
            } else {
                // Show a user-friendly error
                alert('Error registering product. Please try again or skip warranty registration.');
            }

            // Remove active class from submit button
            if (submitContainer) {
                submitContainer.classList.remove('active');
            }
        });
    }

    // Handle review form submission
    handleReviewSubmit(event) {
        event.preventDefault();

        // Ensure we have a submission ID
        if (!this.submissionId) {
            this.showError('Error: Missing submission ID. Please try registering again.');
            return;
        }

        // Show loading indicator
        const submitContainer = event.target.querySelector('.wholescale-submit-review');
        if (submitContainer) {
            submitContainer.classList.add('active');
        }

        // Get form data
        const formData = new FormData(event.target);

        const reviewData = {
            submission: this.submissionId,
            star: formData.get('rating'),
            title: formData.get('title'),
            description: formData.get('detail')
        };

        utils.log('Review Data:', reviewData);

        // For testing only - if in test mode, simulate a successful response
        if (CONFIG.TEST_MODE && CONFIG.DEBUG) {
            utils.log('TEST MODE: Validating review API payload without sending');

            // Validate the payload
            const validationResult = this.validateReviewPayload(reviewData);
            if (!validationResult.isValid) {
                utils.error('Review validation failed:', validationResult.errors);
                alert(`Review API payload validation failed: ${validationResult.errors.join(', ')}`);

                // Remove active class from submit button
                if (submitContainer) {
                    submitContainer.classList.remove('active');
                }
                return;
            }

            // Log what would be sent to the API
            utils.log('API endpoint that would be called:', 'https://be.wholescale.net/review/external_review');
            utils.log('HTTP method:', 'POST');
            utils.log('Headers that would be sent:', {
                'Content-Type': 'application/json'
            });
            utils.log('Request body that would be sent:', JSON.stringify(reviewData, null, 2));

            // Simulate response
            utils.log('Simulated response:', {
                status: 'success',
                message: 'Review submitted successfully (test mode)'
            });

            // Show success toast
            showToast('Review Submitted Successfully', 'success',"", "");
            // Remove active class from submit button before moving to formula finder
            if (submitContainer) {
                submitContainer.classList.remove('active');
            }

            // Make sure submit button isn't focused before hiding
            if (document.activeElement && submitContainer &&
                submitContainer.contains(document.activeElement)) {
                document.activeElement.blur();
            }

            // Delay transition to formula finder to give time for toast to be visible
            setTimeout(() => {
                // Now it's okay to hide the warranty div and go to formula finder
                this.skipToFormulaFinder();
            }, 1500); // Longer delay for test mode to make it easier to see the toast

            return;
        }

        // Otherwise proceed with real API call
        this.submitReviewForm(reviewData, submitContainer);
    }

    // Validate review payload
    validateReviewPayload(data) {
        const errors = [];

        // Check submission ID
        if (!data.submission) errors.push('Submission ID is required');

        // Check star rating
        if (!data.star) {
            errors.push('Rating is required');
        } else if (isNaN(parseInt(data.star)) || parseInt(data.star) < 1 || parseInt(data.star) > 5) {
            errors.push('Rating must be between 1 and 5');
        }

        // Check title
        if (!data.title) errors.push('Title is required');

        // Check description
        if (!data.description) {
            errors.push('Description is required');
        } else if (data.description.length < 50) {
            errors.push('Description must be at least 50 characters');
        }

        return {
            isValid: errors.length === 0,
            errors: errors
        };
    }

    submitReviewForm(reviewData, submitContainer) {
        const url = 'https://be.wholescale.net/review/external_review';

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(reviewData)
        })
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
        })
        .then(data => {
            utils.log('Review response:', data);

            // Show success toast BEFORE removing active class or redirecting
            showToast('Review Submitted Successfully', 'success',"", "");

            // Delay transition slightly to allow toast to appear first
            setTimeout(() => {
                // Remove active class from submit button before moving to formula finder
                if (submitContainer) {
                    submitContainer.classList.remove('active');
                }

                // Make sure submit button isn't focused before hiding
                if (document.activeElement && submitContainer &&
                    submitContainer.contains(document.activeElement)) {
                    document.activeElement.blur();
                }

                // Now it's okay to hide the warranty div and go to formula finder
                this.skipToFormulaFinder();
            }, 500);
        })
        .catch(error => {
            utils.error('Review Error:', error);
            alert('Error submitting review: ' + error.message);

            // Remove active class from submit button
            if (submitContainer) {
                submitContainer.classList.remove('active');
            }
        });
    }

    // Handle No Thanks link click
    handleNoThanksClick(event) {
        event.preventDefault();

        // Blur the link to remove focus
        if (event.target && event.target.blur) {
            event.target.blur();
        }

        this.skipToFormulaFinder();
    }

    // Skip to formula finder function
    skipToFormulaFinder() {
        // First find where we're going to move focus
        const formulaProFilter = this.formulaProFilter || document.getElementById('formula_pro_filter');

        // If we have a formula filter section, focus on it first
        if (formulaProFilter) {
            // Find a good element to focus within the formula filter section
            const focusTarget = formulaProFilter.querySelector('button, [tabindex="0"], a, input, select') || formulaProFilter;

            // Blur any active element to remove focus from form elements
            if (document.activeElement && document.activeElement.blur) {
                document.activeElement.blur();
            }

            // Set tabindex on the target if it's not already focusable
            if (!focusTarget.getAttribute('tabindex')) {
                focusTarget.setAttribute('tabindex', '-1');
            }

            // Move focus to the target element
            focusTarget.focus();

            utils.log('Moved focus to formula finder section before hiding warranty div');
        }

        // Now it's safe to hide the warranty div
        if (this.warrantyRegiDiv) {
            // First remove the show-review class if it exists
            this.warrantyRegiDiv.classList.remove('show-review');

            // Then hide the div and set aria-hidden
            this.warrantyRegiDiv.style.display = 'none';
            this.warrantyRegiDiv.setAttribute('aria-hidden', 'true');
            utils.log('Warranty div hidden with aria-hidden after moving focus');
        }

        // Show formula filter section
        if (formulaProFilter) {
            formulaProFilter.style.display = 'block';
            formulaProFilter.setAttribute('aria-hidden', 'false');

            // Scroll to formula filter section with offset
            utils.scrollToElement('#formula_pro_filter', -105); // Scroll 100px above the element
        }

        // Ensure CTA and results sections remain hidden
        const ctaSection = document.querySelector('.formula-settings-finder__cta');
        if (ctaSection) {
            ctaSection.style.display = 'none';
        }

        const resultsSection = document.querySelector('.formula-settings-finder__results');
        if (resultsSection) {
            resultsSection.style.display = 'none';
        }
    }

    // Force warranty form transition
    forceWarrantyFormTransition() {
        utils.log('Forcing warranty form transition');

        // Check initial state of elements
        const initialWarrantyCss = this.warrantyRegiDiv ? window.getComputedStyle(this.warrantyRegiDiv) : null;
        const initialRegisterCss = this.registerProduct ? window.getComputedStyle(this.registerProduct) : null;
        const initialReviewCss = this.submitReview ? window.getComputedStyle(this.submitReview) : null;

        utils.log('Initial state before transition:', {
            warrantyDisplay: initialWarrantyCss ? initialWarrantyCss.display : 'unknown',
            registerDisplay: initialRegisterCss ? initialRegisterCss.display : 'unknown',
            reviewDisplay: initialReviewCss ? initialReviewCss.display : 'unknown'
        });

        // First, make sure we have the review form element
        if (!this.submitReview) {
            this.submitReview = document.getElementById('submit_review');
            utils.log('Found submit review by ID:', !!this.submitReview);
        }

        // Use setTimeout to ensure this runs after any other code
        setTimeout(() => {
            // 1. Make sure warranty div is visible with !important
            if (this.warrantyRegiDiv) {
                this.warrantyRegiDiv.style.cssText = 'display: block !important; opacity: 1 !important; visibility: visible !important;';
                this.warrantyRegiDiv.removeAttribute('aria-hidden');
                // Add a special class to help with CSS targeting
                this.warrantyRegiDiv.classList.add('show-review');
                utils.log('Force set warrantyRegiDiv display to block !important');
            }

            // 2. Make sure registration form is hidden with !important
            if (this.registerProduct) {
                this.registerProduct.style.cssText = 'display: none !important; visibility: hidden !important;';
                utils.log('Force set registerProduct display to none !important');
            }

            // 3. Make sure review form is visible with !important
            if (this.submitReview) {
                this.submitReview.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important;';
                this.submitReview.classList.add('active');
                utils.log('Force set submitReview display to block !important');

                // Also try to find it by selector and set display directly
                const reviewForms = document.querySelectorAll('.submit-review, #submit_review');
                reviewForms.forEach(form => {
                    form.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important;';
                    form.classList.add('active');
                    utils.log('Force display on additional review form element');
                });
            } else {
                utils.error('Could not find submit review element!');
            }

            // 4. Log the computed styles to verify
            const warrantyCss = this.warrantyRegiDiv ? window.getComputedStyle(this.warrantyRegiDiv) : null;
            const registerCss = this.registerProduct ? window.getComputedStyle(this.registerProduct) : null;
            const reviewCss = this.submitReview ? window.getComputedStyle(this.submitReview) : null;

            utils.log('After initial forcing:', {
                warrantyDisplay: warrantyCss ? warrantyCss.display : 'unknown',
                registerDisplay: registerCss ? registerCss.display : 'unknown',
                reviewDisplay: reviewCss ? reviewCss.display : 'unknown'
            });

            // 5. Double-check after a slight delay to ensure changes took effect
            setTimeout(() => {
                // Try again with even more force
                if (this.warrantyRegiDiv) {
                    // Force through direct DOM manipulation
                    this.warrantyRegiDiv.setAttribute('style', 'display: block !important; opacity: 1 !important; visibility: visible !important;');

                    // Also try jQuery if available
                    if (window.$ && $(this.warrantyRegiDiv).length) {
                        $(this.warrantyRegiDiv).show();
                    }
                }

                if (this.registerProduct) {
                    // Force through direct DOM manipulation
                    this.registerProduct.setAttribute('style', 'display: none !important; visibility: hidden !important;');

                    // Also try jQuery if available
                    if (window.$ && $(this.registerProduct).length) {
                        $(this.registerProduct).hide();
                    }
                }

                if (this.submitReview) {
                    // Force through direct DOM manipulation
                    this.submitReview.setAttribute('style', 'display: block !important; visibility: visible !important; opacity: 1 !important;');
                    this.submitReview.classList.add('active');

                    // Also try jQuery if available
                    if (window.$ && $(this.submitReview).length) {
                        $(this.submitReview).show();
                    }

                    utils.log('Secondary forcing applied to review form');
                } else {
                    // Try to find by ID again and force it
                    const reviewElement = document.getElementById('submit_review');
                    if (reviewElement) {
                        reviewElement.setAttribute('style', 'display: block !important; visibility: visible !important; opacity: 1 !important;');
                        reviewElement.classList.add('active');
                        this.submitReview = reviewElement;
                        utils.log('Found and forced submit_review by ID in secondary check');
                    }
                }

                // Log final state
                const finalWarrantyCss = this.warrantyRegiDiv ? window.getComputedStyle(this.warrantyRegiDiv) : null;
                const finalReviewCss = this.submitReview ? window.getComputedStyle(this.submitReview) : null;

                utils.log('Final state after all attempts:', {
                    warrantyDisplay: finalWarrantyCss ? finalWarrantyCss.display : 'unknown',
                    reviewDisplay: finalReviewCss ? finalReviewCss.display : 'unknown'
                });

                // Force show review form with MutationObserver as a last resort
                if (this.warrantyRegiDiv && this.submitReview) {
                    const observer = new MutationObserver((mutations) => {
                        mutations.forEach((mutation) => {
                            if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                                // If warranty div style changes, ensure it stays visible
                                if (this.warrantyRegiDiv.style.display === 'none') {
                                    this.warrantyRegiDiv.setAttribute('style', 'display: block !important; opacity: 1 !important; visibility: visible !important;');
                                    utils.log('MutationObserver fixed warranty div visibility');
                                }

                                // Make sure review form stays visible
                                if (this.submitReview.style.display === 'none') {
                                    this.submitReview.setAttribute('style', 'display: block !important; visibility: visible !important; opacity: 1 !important;');
                                    utils.log('MutationObserver fixed review form visibility');
                                }
                            }
                        });
                    });

                    // Start observing warranty div and review form for style changes
                    observer.observe(this.warrantyRegiDiv, { attributes: true });
                    observer.observe(this.submitReview, { attributes: true });

                    // Disconnect after 3 seconds
                    setTimeout(() => observer.disconnect(), 3000);
                }
            }, 200);
        }, 100);
    }

    // Hide sections that should start hidden
    hideInitialSections() {
        // Hide warranty registration until product is selected
        const warrantyRegiDiv = document.getElementById('warranty_regi_div');
        if (warrantyRegiDiv) {
            warrantyRegiDiv.style.display = 'none';
            utils.log('Hidden warranty registration on page load');
        }

        // Always hide CTA section
        const ctaSection = document.querySelector('.formula-settings-finder__cta');
        if (ctaSection) {
            ctaSection.style.display = 'none';
            utils.log('Hidden CTA section on page load');
        }

        // Always hide results section
        const resultsSection = document.querySelector('.formula-settings-finder__results');
        if (resultsSection) {
            resultsSection.style.display = 'none';
            utils.log('Hidden results section on page load');
        }

        // Hide formula filter initially
        const formulaProFilter = document.getElementById('formula_pro_filter');
        if (formulaProFilter) {
            formulaProFilter.style.display = 'none';
            utils.log('Hidden formula filter on page load');
        }

        // Hide the dynamic listing section initially
        const dynamicListingSection = document.querySelector('.section--dynamic-listing');
        if (dynamicListingSection) {
            dynamicListingSection.style.display = 'none';
            utils.log('Hidden dynamic listing section on page load');
        }
    }

    // Setup all barcode scan triggers
    setupScanTriggers() {

        // Find the start_upc button if not already found
        if (!this.startUpcButton) {
            this.startUpcButton = document.getElementById('start_upc');
            if (this.startUpcButton) {
                this.startUpcButton.addEventListener('click', () => {
                    this.initBarcodeScanner();
                });
                this.startUpcButton.addEventListener('keypress', (e) => {
                    if (e.key === 'Enter') {
                        this.initBarcodeScanner();
                    }
                });
            }
        }

        // Find and setup all scan trigger elements (including the image)
        const scanTriggers = document.querySelectorAll('.scan-trigger, .image-wrapper.scan-trigger');

        scanTriggers.forEach((trigger, index) => {

            // Remove existing event listeners if any (to prevent duplicates)
            const newTrigger = trigger.cloneNode(true);
            trigger.parentNode.replaceChild(newTrigger, trigger);

            // Add new event listeners
            newTrigger.addEventListener('click', () => {
                this.initBarcodeScanner();
            });

            newTrigger.addEventListener('keypress', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    this.initBarcodeScanner();
                }
            });
        });
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    const formulaSettings = new FormulaSettings();

    // Debug function to check elements
    const debugElements = () => {
        // const productElements = document.querySelectorAll('.formula-settings-banner__product');
        //
        // if (productElements.length > 0) {
        //     productElements.forEach((el, i) => {
        //         console.log(`Product ${i} model type:`, el.dataset.modelType);
        //         console.log(`Product ${i} product ID:`, el.dataset.productId);
        //     });
        // } else {
        //     console.error('No product elements found. Check the class name.');
        // }

        const modelTypeInput = document.getElementById('model_type');
        // console.log('Model Type Input found:', !!modelTypeInput);

        const productSelectionDiv = document.getElementById('productSelectionDiv');
        // console.log('Product Selection Div found:', !!productSelectionDiv);

        const warrantyRegiDiv = document.getElementById('warranty_regi_div');
        // console.log('Warranty Registration Div found:', !!warrantyRegiDiv);

        const formulaProFilter = document.getElementById('formula_pro_filter');
        // console.log('Formula Pro Filter found:', !!formulaProFilter);
    };

    // Run debug after a short delay to ensure DOM is fully loaded
    setTimeout(debugElements, 500);

    // Expose for testing
    if (CONFIG.DEBUG) {
        window.formulaSettings = formulaSettings;
    }
});
